const DEFAULTS = {
    items: '.carousel--item',
    autoPlay: true,
    pauseOnHover: true,
    progressBar: {
        enabled: false,
        className: 'carousel--progress-bar',
    },
    durations: {
        standby: 9000,
        transition: 600,
    },
    item: {
        activeClassName: 'item--active',
        enterClassName: 'item--enter',
        exitClassName: 'item--exit',
    },
    nav: {
        rootClassName: 'carousel--nav',
        holderClassName: 'ui-holder',
        menuClassName: 'bullets-nav-menu',
        menuItemClassName: 'menu-item',
        currentMenuItemClassName: 'current-menu-item',
        buttonClassName: 'bullets-nav-menu-item',
    },
};
const DEBUG = false;
function deepMerge(target, source) {
    Object
        .entries(source)
        .forEach(([key, value]) => {
        if (value && typeof value === 'object') {
            deepMerge(target[key] = target[key] || {}, value);
            return;
        }
        target[key] = value;
    });
    return target;
}
const _log = (...args) => DEBUG && console.log(args);
export default class Carousel {
    constructor(element, options = {}) {
        this._timerId = null;
        this._interTimerId = null;
        this._classNameTimerId = null;
        this._options = deepMerge(DEFAULTS, options);
        this._element = element;
        _log('new Carousel()', this._element, this._options);
        if (!this._element) {
            return;
        }
        this._items = Array.from(this._element.querySelectorAll(this._options.items));
        if (this._items.length > 0) {
            this.initialize();
        }
    }
    initialize() {
        const _this = this;
        const total = this._items.length;
        if (total > 1) {
            this._nav = new Navigation(total, this._options.nav, (index) => _this.show(index));
            const navElement = _this._nav.getElement();
            this._element.appendChild(navElement);
            if (this._options.progressBar.enabled) {
                const { standby, transition } = this._options.durations;
                this._progressBar = new ProgressBar(this._options.progressBar.className, standby, transition);
                const progressBarElement = _this._progressBar.getElement();
                this._element.appendChild(progressBarElement);
            }
            this._items.forEach(item => {
                item.addEventListener('mouseenter', () => this.pause());
                item.addEventListener('mouseleave', () => this.resume());
            });
        }
        this.show(0);
    }
    show(index) {
        const _this = this;
        const { activeClassName, enterClassName, exitClassName } = this._options.item;
        this._timerId && clearInterval(this._timerId);
        this._interTimerId && clearInterval(this._interTimerId);
        this._classNameTimerId && clearInterval(this._classNameTimerId);
        if (!this._items[index] || this._items[index].classList.contains(activeClassName)) {
            return;
        }
        this._progressBar && this._progressBar.reset();
        this._items.forEach((s) => {
            if (!s.classList.contains(activeClassName)) {
                return;
            }
            s.classList.remove(activeClassName);
            s.classList.add(exitClassName);
            _this._classNameTimerId = setInterval(() => {
                _this._items.forEach(_s => _s.classList.remove(exitClassName));
                _this._classNameTimerId && clearInterval(_this._classNameTimerId);
            }, _this._options.durations.transition * 2);
        });
        this._index = index;
        _this._items[index].classList.add(enterClassName);
        _this._nav && this._nav.setActive(_this._index);
        this._interTimerId = setInterval(() => {
            _this._interTimerId && clearInterval(_this._interTimerId);
            _this._items[index].classList.remove(enterClassName);
            _this._items[index].classList.add(activeClassName);
            _this.resume();
        }, _this._options.durations.transition);
    }
    pause() {
        this._progressBar && this._progressBar.pause();
        this._timerId && clearInterval(this._timerId);
    }
    resume() {
        const _this = this;
        this._progressBar && this._progressBar.play();
        this._timerId = setInterval(() => _this.next(), this._options.durations.standby);
    }
    next() {
        if (!this._options.autoPlay) {
            return;
        }
        if (this._index === this._items.length - 1) {
            return this.show(0);
        }
        this.show(this._index + 1);
    }
}
class Navigation {
    constructor(total, options, onItemClick = null) {
        this._total = total;
        this._onItemClick = onItemClick;
        this._options = options;
        this.init();
    }
    init() {
        const _this = this;
        const options = this._options;
        const nav = document.createElement('nav');
        nav.classList.add(options.rootClassName);
        const ul = document.createElement('ul');
        ul.classList.add('menu', options.menuClassName);
        for (let i = 0; i < this._total; i++) {
            const b = document.createElement('button'), li = document.createElement('li');
            li.classList.add(options.menuItemClassName);
            b.setAttribute('data-slide', i.toString());
            b.classList.add(options.buttonClassName, `${options.buttonClassName}-${i}`);
            b.addEventListener('click', (event) => {
                event.preventDefault();
                if (event.currentTarget instanceof HTMLElement) {
                    const el = event.currentTarget;
                    if (el.dataset.slide) {
                        const index = parseInt(el.dataset.slide, 10);
                        _this._onItemClick && _this._onItemClick(index);
                    }
                }
            });
            li.appendChild(b);
            ul.appendChild(li);
        }
        nav.appendChild(ul);
        this._element = nav;
    }
    setActive(index) {
        const options = this._options;
        const bullets = Array.from(this._element.querySelectorAll(`.${options.currentMenuItemClassName}`));
        bullets.forEach(b => b.classList.remove(options.currentMenuItemClassName));
        const navItems = Array.from(this._element.querySelectorAll(`.${options.menuItemClassName}`));
        if (navItems[index]) {
            navItems[index].classList.add(options.currentMenuItemClassName);
        }
    }
    getElement() {
        return this._element;
    }
}
class ProgressBar {
    constructor(className, duration, transitionDuration = 300) {
        this._className = className;
        this._duration = duration;
        this._transitionDuration = transitionDuration;
        this.init();
    }
    init() {
        this._element = document.createElement('div');
        this._element.classList.add(this._className);
    }
    play() {
        this._element.style.transition = `width ${this._duration}ms linear`;
        this._element.style.left = '0';
        this._element.style.right = 'auto';
        this._element.style.width = '100%';
    }
    pause() {
        this._element.style.left = 'auto';
        this._element.style.right = '0';
        this._element.style.width = '0';
    }
    reset() {
        this._element.style.left = 'auto';
        this._element.style.right = '0';
        this._element.style.width = '0';
        this._element.style.transition = `width ${this._transitionDuration}ms linear`;
    }
    getElement() {
        return this._element;
    }
}
;
